// sharingGroups.js - Sharing Groups service
import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs,
  updateDoc, 
  deleteDoc,
  arrayUnion, 
  arrayRemove,
  query,
  where,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Generate a secure random token for sharing group URL
 */
export function generateShareGroupToken() {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let token = '';
  for (let i = 0; i < 16; i++) {
    token += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return token;
}

/**
 * Create a new sharing group
 */
export async function createSharingGroup({
  ownerId,
  name,
  permission, // 'read' | 'write'
  isPublic = false,
  allowedUsers = [] // Array of user IDs
}) {
  const token = isPublic ? generateShareGroupToken() : null;
  
  const sharingGroupData = {
    ownerId,
    name,
    permission,
    isPublic,
    token,
    allowedUsers: allowedUsers || [],
    resources: {
      runs: [],
      racers: [],
      groups: []
    },
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  };

  const sharingGroupRef = doc(collection(db, 'sharingGroups'));
  await setDoc(sharingGroupRef, sharingGroupData);

  return {
    id: sharingGroupRef.id,
    ...sharingGroupData
  };
}

/**
 * Get a sharing group by ID
 */
export async function getSharingGroup(groupId) {
  const groupDoc = await getDoc(doc(db, 'sharingGroups', groupId));
  if (!groupDoc.exists()) return null;
  
  return {
    id: groupDoc.id,
    ...groupDoc.data()
  };
}

/**
 * Get sharing group by token (for public access)
 */
export async function getSharingGroupByToken(token) {
  const groupsRef = collection(db, 'sharingGroups');
  const q = query(groupsRef, where('token', '==', token), where('isPublic', '==', true));
  const snapshot = await getDocs(q);
  
  if (snapshot.empty) return null;
  
  const groupDoc = snapshot.docs[0];
  return {
    id: groupDoc.id,
    ...groupDoc.data()
  };
}

/**
 * Get all sharing groups for a user
 */
export async function getUserSharingGroups(userId) {
  const groupsRef = collection(db, 'sharingGroups');
  const q = query(groupsRef, where('ownerId', '==', userId));
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
}

/**
 * Update a sharing group
 */
export async function updateSharingGroup(groupId, updates) {
  const updatesWithTimestamp = {
    ...updates,
    updatedAt: Timestamp.now()
  };
  
  await updateDoc(doc(db, 'sharingGroups', groupId), updatesWithTimestamp);
}

/**
 * Delete a sharing group
 */
export async function deleteSharingGroup(groupId) {
  await deleteDoc(doc(db, 'sharingGroups', groupId));
}

/**
 * Add resources to a sharing group
 */
export async function addResourcesToSharingGroup(groupId, resources) {
  const group = await getSharingGroup(groupId);
  if (!group) throw new Error('Sharing group not found');

  const updatedResources = { ...group.resources };
  
  // Add resources
  resources.forEach(resource => {
    if (resource.type === 'run' && !updatedResources.runs.includes(resource.id)) {
      updatedResources.runs.push(resource.id);
    } else if (resource.type === 'racer' && !updatedResources.racers.includes(resource.id)) {
      updatedResources.racers.push(resource.id);
    } else if (resource.type === 'group' && !updatedResources.groups.includes(resource.id)) {
      updatedResources.groups.push(resource.id);
    }
  });

  await updateSharingGroup(groupId, { resources: updatedResources });
  
  // Update resources with sharing group reference
  await updateResourcesWithSharingGroup(resources, groupId);
}

/**
 * Remove resources from a sharing group
 */
export async function removeResourcesFromSharingGroup(groupId, resources) {
  const group = await getSharingGroup(groupId);
  if (!group) throw new Error('Sharing group not found');

  const updatedResources = { ...group.resources };
  
  // Remove resources
  resources.forEach(resource => {
    if (resource.type === 'run') {
      updatedResources.runs = updatedResources.runs.filter(id => id !== resource.id);
    } else if (resource.type === 'racer') {
      updatedResources.racers = updatedResources.racers.filter(id => id !== resource.id);
    } else if (resource.type === 'group') {
      updatedResources.groups = updatedResources.groups.filter(id => id !== resource.id);
    }
  });

  await updateSharingGroup(groupId, { resources: updatedResources });
}

/**
 * Update resources with sharing group reference
 */
async function updateResourcesWithSharingGroup(resources, groupId) {
  const updatePromises = [];

  resources.forEach(resource => {
    if (resource.type === 'run') {
      updatePromises.push(
        updateDoc(doc(db, 'events', resource.id), {
          sharingGroups: arrayUnion(groupId)
        })
      );
    } else if (resource.type === 'racer') {
      updatePromises.push(
        updateDoc(doc(db, 'timer_racers', resource.id), {
          sharingGroups: arrayUnion(groupId)
        })
      );
    } else if (resource.type === 'group') {
      // For groups, update all racers in the group
      // This will be handled when fetching racers
    }
  });

  await Promise.all(updatePromises);
}

/**
 * Check if user can access sharing group
 */
export async function canAccessSharingGroup(groupId, userId, isAnonymous = false) {
  const group = await getSharingGroup(groupId);
  if (!group) return false;

  // Owner always has access
  if (group.ownerId === userId) return true;

  // Public groups are accessible to anyone
  if (group.isPublic) return true;

  // Check if user is in allowedUsers list
  if (group.allowedUsers && group.allowedUsers.includes(userId)) return true;

  return false;
}

/**
 * Get share URL for a sharing group
 */
export function getSharingGroupUrl(group, baseUrl = window.location.origin) {
  if (group.isPublic && group.token) {
    return `${baseUrl}/shared-group/${group.token}`;
  }
  return null; // Private groups don't have public URLs
}

/**
 * Calculate scope for a sharing group (all accessible content)
 */
export async function calculateSharingGroupScope(groupId) {
  const group = await getSharingGroup(groupId);
  if (!group) return { runs: [], racers: [], tracks: [], sessions: [], groups: [] };

  const scope = {
    runs: [...(group.resources.runs || [])],
    racers: [...(group.resources.racers || [])],
    tracks: [],
    sessions: [],
    groups: [...(group.resources.groups || [])]
  };

  // For groups, get all racers in those groups
  if (scope.groups.length > 0) {
    const racersRef = collection(db, 'timer_racers');
    const racersSnapshot = await getDocs(racersRef);
    
    racersSnapshot.forEach(docSnap => {
      const racer = docSnap.data();
      const racerGroups = racer.groups || [];
      const hasSharedGroup = racerGroups.some(g => scope.groups.includes(g));
      if (hasSharedGroup && !scope.racers.includes(docSnap.id)) {
        scope.racers.push(docSnap.id);
      }
    });
  }

  // Get all runs where shared racers have actual results
  // Only include runs where at least one shared racer has a result (not just listed)
  const directlySharedRunIds = new Set(scope.runs); // Runs directly shared
  const sharedRacerIds = new Set(scope.racers);
  const accessibleRunIds = new Set(directlySharedRunIds); // Start with directly shared runs
  const accessibleTracks = new Set();
  
  if (scope.racers.length > 0 || scope.runs.length > 0) {
    const runsRef = collection(db, 'events');
    const runsSnapshot = await getDocs(runsRef);
    
    runsSnapshot.forEach(docSnap => {
      const run = docSnap.data();
      const runId = docSnap.id;
      const runRacers = run.racers || [];
      
      // If run is directly shared, always include it
      if (directlySharedRunIds.has(runId)) {
        if (run.track) {
          accessibleTracks.add(run.track);
        }
        return; // Already added
      }
      
      // Otherwise, check if any shared racer has a result in this run
      // A racer has a result if they have a time, position, or any result data
      if (sharedRacerIds.size > 0) {
        const hasSharedRacerWithResult = runRacers.some(racerEntry => {
          const racerId = racerEntry?.racerId;
          if (!racerId || !sharedRacerIds.has(racerId)) return false;
          
          // Check if this racer has actual results (time, position, etc.)
          return racerEntry.time != null || 
                 racerEntry.position != null || 
                 racerEntry.result != null ||
                 racerEntry.duration != null ||
                 (racerEntry.startTime != null && racerEntry.endTime != null);
        });
        
        if (hasSharedRacerWithResult) {
          accessibleRunIds.add(runId);
          
          // Extract track from this run
          if (run.track) {
            accessibleTracks.add(run.track);
          }
        }
      }
    });
    
    scope.runs = Array.from(accessibleRunIds);
    scope.tracks = Array.from(accessibleTracks);
  }

  return scope;
}
